import { Box, TextField, Typography, Button, FormHelperText } from '@mui/material'
import React from 'react'
import { useForm, Controller } from 'react-hook-form'
import { Modal } from '../Modal'
import { CurrencyExchange, Save } from '@mui/icons-material'
import { useRefundByTransaction } from '../../../services/user/useUser'
import { useQueryClient } from 'react-query'
import { TTransaction } from '../../../services/transactions/transaction.service'
import BigNumber from "bignumber.js";

interface FormData {
  description: string;
  amount: string;
}

export default function RefundModal({ open, onClose, userId, transaction }: { open: boolean, onClose: () => void, userId: string, transaction: TTransaction | null }) {

  const queryClient = useQueryClient();
  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    reset
  } = useForm<FormData>({
    defaultValues: {
      description: '',
      amount: ''
    }
  });

  const { mutateAsync: refund, isLoading: isRefundLoading } = useRefundByTransaction();

  const handleAmountBlur = (value: string) => {
    if (value && !isNaN(parseFloat(value))) {
      const formattedAmount = parseFloat(value).toFixed(2);
      setValue('amount', formattedAmount);
    }
  };

  const onSubmit = async (data: FormData) => {
    const refundResult = await refund({
      user_id: userId,
      description: data.description,
      transaction_id: transaction?.transaction_id || '',
      amount: parseFloat(data.amount),
    });
    if (!refundResult.ok) {
      return;
    }
    handleClose();
    queryClient.invalidateQueries(['userDetail', userId]);
    queryClient.invalidateQueries(['list_transaction']);
  };

  const handleClose = () => {
    reset();
    onClose();
  };
  const handleReset = () => {
    reset();
  };

  return (
    <Modal open={open} onClose={handleClose} name="Refund" description='Process a refund for a user transaction' icon={<CurrencyExchange fontSize="large" style={{ color: "yellow" }} />}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 2 }}>
          <Box display={"flex"} flexDirection="column" gap={1}>
            <Typography>Action</Typography>
            <Controller
              name="description"
              control={control}
              rules={{
                required: "Please enter an action",
                minLength: {
                  value: 3,
                  message: "Action must be at least 3 characters"
                }
              }}
              render={({ field }) => (
                <>
                  <TextField
                    {...field}
                    placeholder="Enter action"
                    fullWidth
                    type='text'
                    error={!!errors.description}
                  />
                  <FormHelperText error={!!errors.description}>{errors.description?.message}</FormHelperText>
                </>
              )}
            />
          </Box>

          <Box display={"flex"} flexDirection="column" gap={1}>
            <Typography>Amount</Typography>
            <Controller
              name="amount"
              control={control}
              rules={{
                required: "Please enter an amount",
                validate: {
                  isNumber: (value) => !isNaN(parseFloat(value)) || "Please enter a valid number",
                  isPositive: (value) => BigNumber(value).comparedTo(0) === 1 || "Amount must be greater than 0",
                  maxAmount: (value) => {
                    const transactionAmount = transaction?.amount || 0;
                    const refundedAmount = transaction?.refunded_amount || 0;
                    const maxRefundableAmount = BigNumber(transactionAmount).minus(BigNumber(refundedAmount)).toString();
                    const isAbleToRefund = BigNumber(value).comparedTo(maxRefundableAmount) !== 1;

                    if (!isAbleToRefund) {
                      return `Amount must not exceed ${maxRefundableAmount} (total amount - already refunded)`;
                    }
                    return true;
                  }
                }
              }}
              render={({ field }) => (
                <>
                  <TextField
                    {...field}
                    placeholder="Enter amount"
                    fullWidth
                    onChange={(e) => {
                      const value = e.target.value;
                      // Allow empty string or valid numbers with up to 2 decimal places
                      if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                        field.onChange(value);
                      }
                    }}
                    onBlur={(e) => {
                      handleAmountBlur(e.target.value);
                      field.onBlur();
                    }}
                    inputProps={{
                      inputMode: 'decimal',
                      pattern: '[0-9]*[.,]?[0-9]*'
                    }}
                    InputProps={{
                      endAdornment: <Typography sx={{ mr: 1 }}>S$</Typography>
                    }}
                    error={!!errors.amount}
                  />
                  <FormHelperText error={!!errors.amount}>{errors.amount?.message}</FormHelperText>
                </>
              )}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-around', mt: 2 }}>
            <Button
              type="submit"
              variant="contained"
              sx={{ width: '70%' }}
              startIcon={<Save />}
              disabled={isRefundLoading}
            >
              Save change
            </Button>
            <Button
              variant="outlined"
              onClick={handleReset}
              sx={{ width: '30%' }}
              disabled={isRefundLoading}
            >
              Reset
            </Button>
          </Box>
        </Box>
      </form>
    </Modal>
  )
}
